import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const STORAGE_KEY = "cnc_download_token";

export default function ForumThread() {
  const { id } = useParams();
  const [token] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [name, setName] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/forum/threads/${id}`, { params: { token } });
      setThread(data.thread);
      setReplies(data.replies);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) setDenied(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const postReply = async (e) => {
    e.preventDefault();
    setPosting(true);
    setError("");
    try {
      await api.post(`/forum/threads/${id}/replies`, { token, body: reply, name: name || undefined });
      setReply("");
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not post. Try again.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] text-[#F5F5F5] blueprint-grid px-6 py-16" data-testid="thread-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/forum" className="label-mono hover:text-[#FF3B00] transition-colors flex items-center gap-2" data-testid="thread-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Forum
        </Link>

        {loading ? (
          <div className="mt-12 flex items-center gap-3" data-testid="thread-loading">
            <Loader2 className="w-5 h-5 animate-spin text-[#FF3B00]" />
            <span className="font-mono2 text-sm text-white/60">Loading thread…</span>
          </div>
        ) : denied ? (
          <div className="mt-10 border border-white/15 bg-black/60 p-8" data-testid="thread-denied">
            <p className="text-sm text-white/70 leading-relaxed">
              This thread is for registered members. <Link to="/forum" className="text-[#FF3B00] underline underline-offset-4">Enter the forum</Link> with your registered email first.
            </p>
          </div>
        ) : !thread ? (
          <p className="mt-10 text-white/50 text-sm" data-testid="thread-missing">Thread not found.</p>
        ) : (
          <div className="mt-8" data-testid="thread-content">
            <h1 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl">{thread.title}</h1>
            <p className="font-mono2 text-[11px] text-white/40 mt-3">
              {thread.author_name} · {thread.created_at?.slice(0, 16).replace("T", " ")}
            </p>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="grid-cell p-6 mt-6">
              <p className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed" data-testid="thread-body">{thread.body}</p>
            </motion.div>

            <div className="mt-8 space-y-3" data-testid="reply-list">
              {replies.map((r) => (
                <div key={r.id} className="grid-cell p-5" data-testid={`reply-${r.id}`}>
                  <p className="font-mono2 text-[11px] text-white/40 mb-2">
                    <span className="text-[#FF3B00]">{r.author_name}</span> · {r.created_at?.slice(0, 16).replace("T", " ")}
                  </p>
                  <p className="text-sm text-white/75 whitespace-pre-wrap leading-relaxed">{r.body}</p>
                </div>
              ))}
              {replies.length === 0 && (
                <p className="font-mono2 text-xs text-white/35" data-testid="no-replies">No replies yet.</p>
              )}
            </div>

            <form onSubmit={postReply} className="border border-white/15 bg-black/60 p-6 mt-8 space-y-4" data-testid="reply-form">
              <textarea
                data-testid="reply-body"
                required
                rows={3}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Add a reply…"
                className="w-full bg-transparent border border-white/25 focus:border-[#FF3B00] outline-none p-3 font-mono2 text-sm placeholder:text-white/25 transition-colors resize-none"
              />
              <input
                data-testid="reply-display-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Display name (optional)"
                className="w-full bg-transparent border-b border-white/25 focus:border-[#FF3B00] outline-none py-3 font-mono2 text-sm placeholder:text-white/25 transition-colors"
              />
              {error && <p className="font-mono2 text-xs text-[#FF3B00]" data-testid="reply-error">{error}</p>}
              <button data-testid="reply-submit" type="submit" disabled={posting} className="btn-forge">
                {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post reply"}
              </button>
            </form>
          </div>
        )}
      </div>
    </main>
  );
}
